import { EDITABLE_SHEET_STATUSES, READ_ONLY_SHEET_STATUSES } from './constants';

/**
 * Whether `user` may edit the audit sheet of `chart`.
 *
 * One predicate for every caller that needs the answer, so the audit sheet and
 * the footer navigator can never disagree about what a chart opens as. The
 * chart may come from the chart detail endpoint (`assigned_auditor`) or from a
 * dashboard subRow (`auditor`); both shapes are accepted.
 *
 * Matters more here than it looks: this portal's two status lists do not
 * partition the statuses -- 'AWAITING ASSIGNMENT' is in neither -- so asking
 * "is it read only?" and asking "is it editable?" are not opposites. We require
 * the status to be positively editable, which makes anything unrecognised open
 * read only rather than editable.
 *
 * Deliberately stricter than the API. The backend's edit gate
 * (`get_chart_object_for_edit_or_403_404` in codequick/custom_object_permission.py)
 * denies an edit only when the chart has *both* an auditor and a qa and neither
 * is the caller, so it would let someone save a chart assigned to a different
 * auditor as long as no qa has been set yet. Requiring the assigned auditor to
 * be this user keeps this side of the wire from ever promising an edit the API
 * would refuse.
 */
export function isSheetEditableBy(chart, user) {
  if (!chart || !user) return false;

  const assignedAuditorId = chart.assigned_auditor?.id ?? chart.auditor?.id;
  if (!assignedAuditorId) return false;
  if (String(assignedAuditorId) !== String(user.id)) return false;

  if (READ_ONLY_SHEET_STATUSES.includes(chart.status)) return false;
  return EDITABLE_SHEET_STATUSES.includes(chart.status);
}
